/**
 * Attribute classification for graduated / categorized styling (G42)
 * Equal interval, quantile, natural breaks (Jenks), plus color ramps.
 */

/** Equal-interval class breaks. Returns nClasses+1 boundary values. */
export function equalIntervalBreaks(values, nClasses) {
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const min = sorted[0], max = sorted[sorted.length - 1];
  const step = (max - min) / nClasses || 1;
  return Array.from({ length: nClasses + 1 }, (_, i) => min + i * step);
}

/** Quantile class breaks. Returns nClasses+1 boundary values (fewer if deduped, see M4). */
export function quantileBreaks(values, nClasses) {
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const breaks = [sorted[0]];
  for (let i = 1; i < nClasses; i++) {
    const idx = Math.min(sorted.length - 1, Math.floor((i / nClasses) * sorted.length));
    breaks.push(sorted[idx]);
  }
  breaks.push(sorted[sorted.length - 1]);
  // Many identical values can produce consecutive equal breaks; MapLibre step/interval
  // expressions require strictly ascending stops, so collapse duplicates here (M4).
  return dedupeAscending(breaks);
}

/** Above this input size, jenksBreaks runs its DP on an evenly-strided sample instead
 *  of the full dataset (M3) — the O(n^2*k) dynamic program otherwise freezes the render
 *  thread for seconds on large (30k+) layers every time a StyleEditor slider changes. */
const JENKS_SAMPLE_THRESHOLD = 2000;

/** Evenly-strided sample of a sorted array, always keeping the first and last elements. */
function sampleEvenly(sorted, targetSize) {
  const n = sorted.length;
  if (n <= targetSize) return sorted;
  const step = (n - 1) / (targetSize - 1);
  const sample = new Array(targetSize);
  for (let i = 0; i < targetSize; i++) {
    sample[i] = sorted[Math.round(i * step)];
  }
  return sample;
}

/**
 * Jenks natural breaks (classic dynamic-programming implementation).
 * Returns nClasses+1 boundary values (fewer if deduped, see M4).
 */
export function jenksBreaks(values, nClasses) {
  const sorted = [...values].sort((a, b) => a - b);
  const total = sorted.length;
  if (total === 0) return [];
  if (total <= nClasses) return [...sorted, sorted[total - 1]];

  // Bound the DP's cost on large layers by computing over a representative sample —
  // the resulting breaks are near-identical since Jenks only depends on the overall
  // distribution shape, not every individual value.
  const data = total > JENKS_SAMPLE_THRESHOLD ? sampleEvenly(sorted, JENKS_SAMPLE_THRESHOLD) : sorted;
  const n = data.length;

  // Matrices: lowerClassLimits + variance combinations
  const lower = Array.from({ length: n + 1 }, () => new Array(nClasses + 1).fill(0));
  const variance = Array.from({ length: n + 1 }, () => new Array(nClasses + 1).fill(0));

  for (let i = 1; i <= nClasses; i++) {
    lower[1][i] = 1;
    variance[1][i] = 0;
    for (let j = 2; j <= n; j++) {
      variance[j][i] = Infinity;
    }
  }

  for (let l = 2; l <= n; l++) {
    let sum = 0, sumSq = 0, w = 0;
    let varClass = 0;
    for (let m = 1; m <= l; m++) {
      const lowerIdx = l - m + 1;
      const val = data[lowerIdx - 1];
      w++;
      sum += val;
      sumSq += val * val;
      varClass = sumSq - (sum * sum) / w;
      const i4 = lowerIdx - 1;
      if (i4 !== 0) {
        for (let j = 2; j <= nClasses; j++) {
          if (variance[l][j] >= varClass + variance[i4][j - 1]) {
            lower[l][j] = lowerIdx;
            variance[l][j] = varClass + variance[i4][j - 1];
          }
        }
      }
    }
    lower[l][1] = 1;
    variance[l][1] = varClass;
  }

  const breaks = new Array(nClasses + 1);
  breaks[nClasses] = data[n - 1];
  breaks[0] = data[0];
  let k = n;
  for (let j = nClasses; j >= 2; j--) {
    breaks[j - 1] = data[lower[k][j] - 2];
    k = lower[k][j] - 1;
  }
  // Skewed data (or the sampling above) can still yield consecutive equal breaks —
  // collapse them so downstream step expressions stay strictly ascending (M4).
  return dedupeAscending(breaks);
}

/** Drop consecutive duplicate values so breaks are strictly ascending. MapLibre's
 *  step/interval expressions require strictly ascending stops — a duplicate silently
 *  rejects the whole style and the layer renders unstyled (M4). This can legitimately
 *  reduce the effective class count when many identical values collide on one break. */
function dedupeAscending(breaks) {
  // Heavily duplicate-heavy inputs can make the classic Jenks DP reconstruction miss a
  // valid index and emit a non-finite stop; filter those out too, not just equal-adjacent
  // values, since an undefined/NaN stop breaks MapLibre step expressions just as badly.
  const finite = breaks.filter(v => typeof v === 'number' && isFinite(v));
  if (finite.length <= 1) return finite;
  const out = [finite[0]];
  for (let i = 1; i < finite.length; i++) {
    if (finite[i] !== out[out.length - 1]) out.push(finite[i]);
  }
  return out;
}

/** Compute breaks by method name: 'equal' | 'quantile' | 'jenks'. */
export function computeBreaks(values, nClasses, method = 'quantile') {
  const clean = values.filter(v => typeof v === 'number' && isFinite(v));
  if (clean.length === 0) return [];
  switch (method) {
    case 'equal': return equalIntervalBreaks(clean, nClasses);
    case 'jenks': return jenksBreaks(clean, nClasses);
    case 'quantile':
    default: return quantileBreaks(clean, nClasses);
  }
}

// ── Color ramps ──────────────────────────────────────────────────────────────

export const COLOR_RAMPS = {
  // Sequential
  viridis:  { label: 'Viridis',  type: 'sequential', colors: ['#440154', '#414487', '#2a788e', '#22a884', '#7ad151', '#fde725'] },
  plasma:   { label: 'Plasma',   type: 'sequential', colors: ['#0d0887', '#6a00a8', '#b12a90', '#e16462', '#fca636', '#f0f921'] },
  blues:    { label: 'Blues',    type: 'sequential', colors: ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c', '#08306b'] },
  heat:     { label: 'Heat',     type: 'sequential', colors: ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#fb923c', '#f87171'] },
  // Diverging
  rdbu:     { label: 'Red–Blue', type: 'diverging',  colors: ['#b2182b', '#ef8a62', '#fddbc7', '#d1e5f0', '#67a9cf', '#2166ac'] },
  spectral: { label: 'Spectral', type: 'diverging',  colors: ['#d53e4f', '#fc8d59', '#fee08b', '#e6f598', '#99d594', '#3288bd'] },
  // Qualitative
  bright:   { label: 'Bright',   type: 'qualitative', colors: ['#00d4ff', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#38bdf8', '#fb923c', '#4ade80', '#e879f9', '#8b949e'] },
  pastel:   { label: 'Pastel',   type: 'qualitative', colors: ['#a5f3fc', '#bbf7d0', '#fef08a', '#fecaca', '#ddd6fe', '#bae6fd', '#fed7aa', '#d9f99d', '#fbcfe8', '#e2e8f0'] },
};

/** Sample n colors evenly from a ramp. */
export function sampleRamp(rampKey, n) {
  const ramp = COLOR_RAMPS[rampKey] || COLOR_RAMPS.viridis;
  const colors = ramp.colors;
  if (n <= 1) return [colors[0]];
  if (ramp.type === 'qualitative') {
    return Array.from({ length: n }, (_, i) => colors[i % colors.length]);
  }
  // Interpolate evenly across the ramp
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    const pos = t * (colors.length - 1);
    const lo = Math.floor(pos), hi = Math.ceil(pos);
    if (lo === hi) return colors[lo];
    return lerpColor(colors[lo], colors[hi], pos - lo);
  });
}

function lerpColor(a, b, t) {
  const pa = hexToRgb(a), pb = hexToRgb(b);
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// ── MapLibre expression builders ─────────────────────────────────────────────

/**
 * Build a MapLibre step expression for graduated (numeric) styling.
 * @param {string} attr - property name
 * @param {number[]} breaks - class boundaries (n+1 values)
 * @param {string[]} colors - n colors
 */
export function graduatedColorExpression(attr, breaks, colors) {
  // Defensive dedupe: guarantees strictly-ascending stops (M4) no matter which
  // classification method produced `breaks`, or whether it already went through
  // computeBreaks. Colors still map correctly below — indices just clamp to the
  // (possibly smaller) number of classes that remain.
  const stops = dedupeAscending((breaks || []).filter((b) => typeof b === 'number' && Number.isFinite(b)));
  if (stops.length < 2 || colors.length === 0) return colors[0] || '#00d4ff';
  const expr = ['step', ['to-number', ['get', attr], stops[0]], colors[0]];
  for (let i = 1; i < stops.length - 1; i++) {
    expr.push(stops[i], colors[Math.min(i, colors.length - 1)]);
  }
  return expr;
}

/**
 * Build a MapLibre match expression for categorized (string) styling.
 * @param {string} attr - property name
 * @param {string[]} categories - unique values
 * @param {string[]} colors - one per category
 * @param {string} fallback - color for unmatched values
 */
export function categorizedColorExpression(attr, categories, colors, fallback = '#6e7681') {
  if (categories.length === 0) return fallback;
  const expr = ['match', ['to-string', ['get', attr]]];
  categories.forEach((cat, i) => {
    expr.push(String(cat), colors[i % colors.length]);
  });
  expr.push(fallback);
  return expr;
}

/** Unique category values of an attribute (max 30, by frequency). */
export function uniqueCategories(features, attr, max = 30) {
  const counts = {};
  features.forEach(f => {
    const v = f.properties?.[attr];
    if (v == null || v === '') return;
    const key = String(v);
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([k]) => k);
}

/** Numeric values of an attribute across features. */
export function numericValues(features, attr) {
  return features
    .map(f => parseFloat(f.properties?.[attr]))
    .filter(v => !isNaN(v) && isFinite(v));
}
